import React, { useCallback, useEffect, useReducer } from 'react';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import {
    Box,
    FileDropzone,
    FileInput,
    FileInputLabel,
    FormHelper,
    GridContainer,
    Icon,
    Row,
} from '@vp/swan';
import { bugtracker, useFileUploader, useIdentityContext } from '@99designs/design-services-common';
import { StructuredBriefFile } from '@99designs/graph-utils/types';
import { __ } from '@99designs/i18n';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { Field_FilesField_Fragment } from '../../brief.generated';
import { UploadedFileTile, UploadingFileTile } from './FileTiles';
import {
    filterFiles,
    mapErrorMessages,
    mapFileToFileKey,
    mapUploadFilesToBriefFiles,
} from './mappers';
import { fileReducer } from './state';
import { useUpdateFilesField } from './useUpdateFilesField';

export type FilesFieldProps = Field_FilesField_Fragment & {
    register: UseFormRegister<any>;
    setValue: UseFormSetValue<any>;
};

export function Input({
    id,
    required,
    subtext,
    allowedFileTypes,
    filesValue,
    register,
    setValue,
    errorMessage,
}: FilesFieldProps) {
    const { briefId, isUploadAndGo } = useBriefFormContext();
    const errorMsg = errorMessage || __('This field is required');
    // Related to files uploader/backend operations:

    const { accessToken } = useIdentityContext();
    const { error: uploadError, uploadFiles, deleteFile } = useFileUploader(accessToken);

    // Related to files state:

    const [files, filesDispatch] = useReducer(fileReducer, {
        uploadingFiles: [],
        uploadedFiles: [],
        error: null,
    });

    // Related to files on form state:

    const updateMutation = useUpdateFilesField(id, briefId, filesValue);

    // Update uploaded files state only _once_ when the component mounts. This avoids edge cases
    // where rapid deletions/uploads cause the state to be out of sync with the backend.

    useEffect(() => {
        setValue(id, filesValue);
        filesDispatch({
            type: 'COMPLETE_UPLOAD',
            payload: { keys: [], files: filesValue },
        });
        // eslint-disable-next-line
    }, []);

    // If the backend reports an error on file upload (not brief form state update), set error.

    useEffect(() => {
        if (uploadError) {
            filesDispatch({
                type: 'SET_ERROR',
                payload: __(`Sorry couldn't upload files. Please try again.`),
            });
        }
    }, [uploadError]);

    // Register virtual field.

    useEffect(() => {
        register(id, {
            required: required ? errorMsg : false,
        });
    }, []);

    /**
     * Upload files in file `input` all at once, then clear the HTML5 input value.
     *
     * @param event {React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>} the event that triggered the upload
     */
    const handleFileChange = useCallback(
        async (
            event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>
        ): Promise<void> => {
            event.preventDefault();
            event.stopPropagation();

            const newFiles =
                'dataTransfer' in event ? event.dataTransfer.files : event.target.files;
            const filteredNewFiles = filterFiles(newFiles, allowedFileTypes || []);

            if (newFiles && newFiles.length > 0) {
                // Check if some files were filtered out due to file type or size restrictions.

                if (filteredNewFiles.length !== newFiles.length) {
                    filesDispatch({
                        type: 'SET_ERROR',
                        payload: mapErrorMessages(allowedFileTypes || []),
                    });

                    return;
                }

                const uploadingFiles = filteredNewFiles.map((file) => {
                    return {
                        key: mapFileToFileKey(file),
                        file,
                    };
                });

                filesDispatch({
                    type: 'START_UPLOAD',
                    payload: { files: uploadingFiles, isSingleFileOnly: isUploadAndGo },
                });

                try {
                    const newUploadedFiles = mapUploadFilesToBriefFiles(
                        await uploadFiles(filteredNewFiles)
                    );
                    setValue(id, newUploadedFiles, { shouldValidate: true });

                    filesDispatch({
                        type: 'COMPLETE_UPLOAD',
                        payload: {
                            keys: uploadingFiles.map((file) => file.key),
                            files: newUploadedFiles,
                            isSingleFileOnly: isUploadAndGo,
                        },
                    });

                    const retainedUploadedFiles = isUploadAndGo ? [] : files.uploadedFiles;
                    const updatedFiles = [...retainedUploadedFiles, ...newUploadedFiles];
                    const filesToUpdate = updatedFiles.map(
                        ({ downloadUrl: _downloadUrl, previewUrl: _previewUrl, ...rest }) => rest
                    );
                    const mutationResult = await updateMutation(filesToUpdate);
                    if (mutationResult?.errors) {
                        filesDispatch({
                            type: 'SET_ERROR',
                            payload: __(`Sorry couldn't upload files. Please try again.`),
                        });

                        bugtracker.notify(
                            new Error('error while adding to files field on the server: '),
                            (event) => {
                                event.addMetadata('debug info', 'brief_id', briefId);
                                event.addMetadata('debug info', 'mutation_result', mutationResult);
                                event.addMetadata('debug info', 'files', filesToUpdate);
                                event.addMetadata('debug info', 'errors', mutationResult.errors);
                            }
                        );

                        return;
                    }

                    setValue(id, updatedFiles);
                } finally {
                    // Behind the scenes, this is just an input element for files, meaning if the same
                    // files were to be uploaded again, the event would not be triggered. This is why
                    // we need to clear the value of the input element to allow the same files to be
                    // uploaded again.

                    // This only affects the manual upload; not drag and drop. We still do not allow
                    // uploading the same file if it is already being uploaded.

                    if (!('dataTransfer' in event)) {
                        event.target.value = '';
                    }
                }
            }
        },
        [briefId, allowedFileTypes, files.uploadedFiles, id, setValue, updateMutation, uploadFiles]
    );

    /**
     * Handle deletion of a single file.
     *
     * @param file {StructuredBriefFile} the file to remove
     */
    const handleFileRemove = useCallback(
        async (file: StructuredBriefFile) => {
            filesDispatch({
                type: 'START_DELETE',
                payload: { key: file.key },
            });

            const updatedFiles = files.uploadedFiles.filter((f) => f.key !== file.key);
            const filesToUpdate = updatedFiles.map(
                ({ downloadUrl: _downloadUrl, previewUrl: _previewUrl, ...rest }) => rest
            );
            const mutationResult = await updateMutation(filesToUpdate);
            if (mutationResult?.errors) {
                filesDispatch({
                    type: 'SET_ERROR',
                    payload: __(`Sorry, couldn't delete files. Please try again.`),
                });

                bugtracker.notify(
                    new Error('error while removing from files field on the server: '),
                    (event) => {
                        event.addMetadata('debug info', 'brief_id', briefId);
                        event.addMetadata('debug info', 'mutation_result', mutationResult);
                        event.addMetadata('debug info', 'file', file);
                        event.addMetadata('debug info', 'errors', mutationResult.errors);
                    }
                );

                return;
            }

            // Deleting the file from backend is necessary for cleanup but not for the UI. If we
            // fail to delete the file, we still have valid brief form data.
            //
            // Do note, however, that this can cause the error to appear even if the file is
            // successfully removed from the brief form state, which can be confusing.

            filesDispatch({
                type: 'COMPLETE_DELETE',
                payload: { key: file.key },
            });

            await deleteFile(file.key);
        },
        [briefId, deleteFile, files.uploadedFiles, updateMutation]
    );

    return (
        <Box key={id} marginBottom="5">
            <Box>
                <FileDropzone
                    note={__('or drag and drop here')}
                    dropNote={__('Drop your files here')}
                    multiFileErrorMessage={__('Please upload only one file')}
                    aria-label={__('Dropzone')}
                >
                    <FileInput
                        data-testid="file-input"
                        type="file"
                        accept={allowedFileTypes ? allowedFileTypes.join(', ') : ''}
                        multiple={!isUploadAndGo}
                        onChange={handleFileChange}
                        aria-required={required ? true : undefined}
                        aria-describedby={`${id}-size-error`}
                    >
                        <FileInputLabel iconPosition="left">
                            <Icon iconType="upload" /> {__('Upload from device')}
                        </FileInputLabel>
                    </FileInput>
                </FileDropzone>
                <FormHelper
                    role="alert"
                    textColor={files.error ? 'error' : 'subtle'}
                    marginTop={3}
                    marginBottom={5}
                    id={`${id}-size-error`}
                >
                    {files.error || subtext}
                </FormHelper>
            </Box>
            <GridContainer>
                <Row>
                    {files.uploadedFiles.map((file, index) => (
                        <UploadedFileTile
                            key={`${file.name}-${index}`}
                            file={file}
                            onDelete={() => handleFileRemove(file)}
                            isDeleteAllowed={!required || files.uploadedFiles.length > 1}
                        />
                    ))}
                    {files.uploadingFiles.map((uploadingFile, index) => (
                        <UploadingFileTile
                            key={`uploading-${mapFileToFileKey(uploadingFile.file)}-${index}`}
                            file={uploadingFile.file}
                        />
                    ))}
                </Row>
            </GridContainer>
        </Box>
    );
}
